import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, memo, useState } from "react";
import { Link } from "react-router";
import { useApiQuery } from "../../hooks/use-api-query";
import { theme } from "../../theme/theme";
import type {
  CheckStatusSummary,
  DailyStatus,
  OverallStatusType,
  ScheduleStatusSummary,
  TestRunStatus,
} from "../../types";
import { StatusChip, type StatusEntry } from "../ui/status-chip";
import { UptimeBar } from "./uptime-bar";

const STATUS_CHIP_CONFIG: Record<
  OverallStatusType,
  { label: string; color: "success" | "warning" | "error" | "default" }
> = {
  operational: { label: "Operational", color: "success" },
  degraded: { label: "Degraded", color: "warning" },
  outage: { label: "Outage", color: "error" },
  unknown: { label: "Unknown", color: "default" },
};

const OverallStatusChip: FC<{ status: OverallStatusType }> = ({ status }) => {
  const config = STATUS_CHIP_CONFIG[status];
  return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
};

const RUN_STATUS_MAP: Partial<Record<TestRunStatus, StatusEntry>> = {
  passed: { color: "success" },
  failed: { color: "error" },
  error: { color: "error" },
  running: { color: "info" },
  pending: { color: "default" },
  timed_out: { color: "warning" },
  cancelled: { color: "default" },
  flaky: { color: "warning" },
};

const CheckRunStatusChip: FC<{ status?: TestRunStatus }> = ({ status }) => {
  if (!status) return null;
  return <StatusChip status={status} statusMap={RUN_STATUS_MAP} />;
};

interface CheckStatusRowProps {
  check: CheckStatusSummary;
  selectedDate?: string;
  onDayClick?: (status: DailyStatus, checkId: string, checkName: string) => void;
}

const CheckStatusRow: FC<CheckStatusRowProps> = memo(({ check, selectedDate, onDayClick }) => {
  const handleDayClick = (status: DailyStatus) => {
    onDayClick?.(status, check.check_id, check.check_name);
  };

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.grey[50], 0.5),
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pl: 4 }}>
          <Typography
            component={Link}
            to={`/checks/${check.check_id}`}
            variant="body2"
            fontWeight={500}
            sx={{
              color: "grey.800",
              textDecoration: "none",
              "&:hover": {
                color: "primary.main",
                textDecoration: "underline",
              },
            }}
          >
            {check.check_name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {check.uptime_percentage.toFixed(1)}% uptime
            </Typography>
            <CheckRunStatusChip status={check.current_status} />
          </Stack>
        </Stack>
        <UptimeBar
          dailyStatuses={check.daily_statuses}
          showLabels={false}
          selectedDate={selectedDate}
          onDayClick={handleDayClick}
        />
      </Stack>
    </Box>
  );
});

CheckStatusRow.displayName = "CheckStatusRow";

const CheckStatusRowSkeleton: FC = () => (
  <Box
    sx={{
      px: 2.5,
      py: 2,
      borderTop: "1px solid",
      borderColor: "divider",
      bgcolor: alpha(theme.palette.grey[50], 0.5),
    }}
  >
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pl: 4 }}>
        <Skeleton variant="text" width={120} height={20} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={70} height={16} />
          <Skeleton variant="rounded" width={60} height={24} />
        </Stack>
      </Stack>
      <Skeleton variant="rounded" height={24} sx={{ borderRadius: 1 }} />
    </Stack>
  </Box>
);

interface ScheduleStatusCardProps {
  schedule: ScheduleStatusSummary;
  days: number;
  selectedDate?: string;
  onScheduleDayClick?: (status: DailyStatus) => void;
  onCheckDayClick?: (status: DailyStatus, checkId: string, checkName: string) => void;
}

export const ScheduleStatusCard: FC<ScheduleStatusCardProps> = memo(
  ({ schedule, days, selectedDate, onScheduleDayClick, onCheckDayClick }) => {
    const [expanded, setExpanded] = useState(false);

    // Fetch checks lazily when expanded
    const { data: checks, isLoading: checksLoading } = useApiQuery<CheckStatusSummary[]>(
      `/status-page/${schedule.schedule_id}/checks?days=${days}`,
      {
        enabled: expanded,
      },
    );

    const hasChecks = schedule.total_checks > 0;

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "grey.400",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          },
        }}
      >
        <CardContent sx={{ pb: expanded ? 0 : undefined, p: 2.5 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center">
                {hasChecks && (
                  <IconButton
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                      ml: -1,
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      },
                    }}
                  >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
                <Box>
                  <Typography
                    component={Link}
                    to={`/check-schedules/${schedule.schedule_id}`}
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: "text.primary",
                      textDecoration: "none",
                      display: "block",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {schedule.schedule_name}
                  </Typography>
                  {schedule.description && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {schedule.description}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                  {schedule.uptime_percentage.toFixed(1)}% uptime
                </Typography>
                <OverallStatusChip status={schedule.current_status} />
              </Stack>
            </Stack>

            <UptimeBar
              dailyStatuses={schedule.daily_statuses}
              selectedDate={selectedDate}
              onDayClick={onScheduleDayClick}
            />
          </Stack>
        </CardContent>

        {hasChecks && (
          <Collapse in={expanded}>
            <Box>
              {checksLoading ? (
                Array.from({ length: schedule.total_checks }).map((_, i) => (
                  <CheckStatusRowSkeleton key={i} />
                ))
              ) : checks && checks.length > 0 ? (
                checks.map((check) => (
                  <CheckStatusRow
                    key={check.check_id}
                    check={check}
                    selectedDate={selectedDate}
                    onDayClick={onCheckDayClick}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    py: 2,
                    textAlign: "center",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No checks found
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        )}
      </Card>
    );
  },
);

ScheduleStatusCard.displayName = "ScheduleStatusCard";
